import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Card, TextField } from "@mui/material";
import SoftBox from "./SoftBox";
import SoftTypography from "./SoftTypography";
import SoftButton from "./SoftButton";

export default function TypingTest({
  assessment,
  handleNextStep,
  region,
  questions,
  category,
  test,
  nextSectionName,
}) {
  const [typedText, setTypedText] = useState("");
  const [isStarted, setIsStarted] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);

  // Keystroke tracking for anti-cheat
  const keystrokeTimestamps = useRef([]);
  const textAreaRef = useRef(null);
  const timerRef = useRef(null);

  const passage = questions && questions.length > 0 ? questions[0] : null;
  const textToType = passage?.text_to_type || "";
  const timeLimit = passage?.time_limit || 180;
  const title = passage?.title || "Typing Test";

  // Timer effect
  useEffect(() => {
    if (isStarted && !isFinished) {
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsed);

        if (elapsed >= timeLimit) {
          clearInterval(timerRef.current);
          setIsFinished(true);
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isStarted, isFinished, startTime, timeLimit]);

  // Calculate WPM and accuracy in real time
  useEffect(() => {
    if (!isStarted || elapsedSeconds === 0) return;

    // Accuracy: correct chars / chars actually typed (not full passage)
    let correctChars = 0;
    const compareLength = Math.min(typedText.length, textToType.length);
    for (let i = 0; i < compareLength; i++) {
      if (typedText[i] === textToType[i]) {
        correctChars++;
      }
    }
    setAccuracy(typedText.length > 0 ? Math.round((correctChars / typedText.length) * 100) : 100);

    // Net WPM: industry standard — only correct chars count toward speed
    const minutes = elapsedSeconds / 60;
    setWpm(minutes > 0 ? Math.round((correctChars / 5) / minutes) : 0);
  }, [typedText, elapsedSeconds, isStarted, textToType]);

  const handleTyping = useCallback(
    (e) => {
      if (isFinished) return;

      if (!isStarted) {
        setIsStarted(true);
        setStartTime(Date.now());
      }

      // Record keystroke timestamp
      keystrokeTimestamps.current.push(Date.now());

      const newText = e.target.value;
      setTypedText(newText);

      // Stop timer when candidate has typed the full passage
      if (textToType && newText.length >= textToType.length) {
        setIsFinished(true);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    },
    [isStarted, isFinished, textToType]
  );

  // Anti-cheat: block paste, copy, cut, drag/drop
  const blockEvent = (e) => {
    e.preventDefault();
    return false;
  };

  // Anti-cheat: block keyboard shortcuts for paste
  const handleKeyDown = (e) => {
    if (isFinished) {
      e.preventDefault();
      return;
    }
    // Block Ctrl+V, Ctrl+Shift+V, Ctrl+A (select all to paste over)
    if (e.ctrlKey || e.metaKey) {
      if (e.key === "v" || e.key === "V") {
        e.preventDefault();
        return;
      }
    }
  };

  // Calculate keystroke statistics for anti-cheat analysis
  const getKeystrokeStats = () => {
    const timestamps = keystrokeTimestamps.current;
    if (timestamps.length < 2) {
      return { count: timestamps.length, avgInterval: 0, variance: 0 };
    }
    const intervals = [];
    for (let i = 1; i < timestamps.length; i++) {
      intervals.push(timestamps[i] - timestamps[i - 1]);
    }
    const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance =
      intervals.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) /
      intervals.length;
    return {
      count: timestamps.length,
      avgInterval: Math.round(avg),
      variance: Math.round(variance),
    };
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setIsFinished(true);
    if (timerRef.current) clearInterval(timerRef.current);

    const keystrokeStats = getKeystrokeStats();

    // Calculate final accuracy — correct chars / chars actually typed
    let correctChars = 0;
    const compareLength = Math.min(typedText.length, textToType.length);
    for (let i = 0; i < compareLength; i++) {
      if (typedText[i] === textToType[i]) correctChars++;
    }
    const finalAccuracy =
      typedText.length > 0
        ? Math.round((correctChars / typedText.length) * 100)
        : 0;

    // Net WPM: industry standard — only correct chars count toward speed
    const finalElapsed = elapsedSeconds || 1;
    const finalMinutes = finalElapsed / 60;
    const finalWpm = finalMinutes > 0 ? Math.round((correctChars / 5) / finalMinutes) : 0;

    const typingResult = {
      typed_text: typedText,
      wpm: finalWpm,
      accuracy: finalAccuracy,
      elapsed_seconds: finalElapsed,
      keystroke_count: keystrokeStats.count,
      avg_keystroke_interval: keystrokeStats.avgInterval,
      keystroke_variance: keystrokeStats.variance,
    };

    // CDT-309: a failure here used to go to the console and no further — no
    // retry, no message, and no advance. A candidate whose result did not
    // save sat on a finished screen with nothing to tell her why, which is
    // the same silent loss the written sections were fixed for.
    const MAX_ATTEMPTS = 3;
    let saved = false;
    let lastProblem = "";
    let serverMessage = "";

    setSaveError("");

    for (let attempt = 1; attempt <= MAX_ATTEMPTS && !saved; attempt++) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/update_grammar_scores`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              assessment_uid: assessment.uid,
              current_step: "typing_test",
              answers: [typingResult],
            }),
          }
        );

        if (response.ok) {
          saved = true;
          break;
        }

        lastProblem = `HTTP ${response.status}`;
        console.error("Failed to submit typing test scores:", response.statusText);

        // A 4xx will not succeed on a retry. Take the server's reason if it
        // gave one, rather than blaming the connection.
        if (response.status >= 400 && response.status < 500) {
          try {
            const body = await response.json();
            if (body && body.error) serverMessage = body.error;
          } catch (e) {
            // No JSON body — the generic wording below still applies.
          }
          break;
        }
      } catch (error) {
        lastProblem = error.message || "network error";
        console.error("Error submitting typing test:", error);
      }

      if (attempt < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, attempt * 1500));
      }
    }

    if (saved) {
      handleNextStep();
    } else {
      setSaveError(
        serverMessage ||
        `We could not save your typing test (${lastProblem}). ` +
        `Please check your internet connection and try again.`
      );
    }

    setIsSubmitting(false);
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const remainingTime = Math.max(0, timeLimit - elapsedSeconds);

  // Render the passage with character-level highlighting
  const renderHighlightedPassage = () => {
    if (!textToType) return null;

    return (
      <div
        style={{
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: "16px",
          lineHeight: "1.8",
          padding: "20px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          maxHeight: "200px",
          overflowY: "auto",
          wordWrap: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {textToType.split("").map((char, i) => {
          let color = "#64748b"; // Default grey (not yet typed)
          let backgroundColor = "transparent";
          let textDecoration = "none";

          if (i < typedText.length) {
            if (typedText[i] === char) {
              color = "#16a34a"; // Green for correct
            } else {
              color = "#dc2626"; // Red for incorrect
              backgroundColor = "#fee2e2";
              textDecoration = "underline";
            }
          } else if (i === typedText.length) {
            // Current cursor position
            backgroundColor = "#dbeafe";
          }

          return (
            <span
              key={i}
              style={{
                color,
                backgroundColor,
                textDecoration,
                transition: "all 0.05s ease",
              }}
            >
              {char}
            </span>
          );
        })}
      </div>
    );
  };

  if (!passage) {
    return (
      <Container style={{ marginTop: "20px", padding: "20px" }}>
        <Card style={{ padding: "30px", textAlign: "center", border: "2px solid #dc2626" }}>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "#dc2626", marginBottom: "12px" }}>
            Typing Test: No Questions Found
          </div>
          <div style={{ fontSize: "16px", color: "#64748b", marginBottom: "20px" }}>
            Unable to load the typing test. Please contact your administrator.
          </div>
          <SoftButton
            variant="contained"
            color="gt_primary"
            onClick={handleNextStep}
            style={{ marginTop: "20px" }}
          >
            Skip to Next Section
          </SoftButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        padding: "0px !important",
        margin: "-30px auto 20px auto !important",
      }}
    >
      {/* Header */}
      <Container
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "0px",
          padding: "0px !important",
          margin: "-30px auto 20px auto !important",
        }}
      >
        <div style={{ backgroundColor: "#FFFFFF", padding: "0 20px 5px 20px" }}>
          <SoftBox mt={1} mb={1} style={{ backgroundColor: "#FFFFFF" }}>
            <div style={{ display: "flex" }}>
              <span
                style={{
                  flex: "0.6",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "rgba(79, 79, 79, 1)",
                }}
              >
                Typing Test
              </span>
            </div>
          </SoftBox>
          <SoftBox mb={1} style={{ backgroundColor: "#FFFFFF" }}>
            <SoftTypography
              variant="body2"
              fontWeight="medium"
              color="dark"
              style={{
                color: "rgba(132, 132, 132, 1)",
                fontSize: "16px",
                fontWeight: "400",
              }}
            >
              Type the passage shown below as accurately and quickly as you can.
              Copy and paste is disabled.
            </SoftTypography>
          </SoftBox>
        </div>
      </Container>

      <Container sx={{ borderRadius: "0px", padding: "0px !important" }}>
        {/* Stats Bar */}
        <Card
          style={{
            marginBottom: "16px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
          }}
        >
          <SoftBox
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: "12px" }}
          >
            {/* Timer */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor:
                    remainingTime <= 30
                      ? "#fee2e2"
                      : remainingTime <= 60
                      ? "#fef3c7"
                      : "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                }}
              >
                ⏱
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Time Left
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color:
                      remainingTime <= 30
                        ? "#dc2626"
                        : remainingTime <= 60
                        ? "#d97706"
                        : "#1e293b",
                  }}
                >
                  {formatTime(remainingTime)}
                </div>
              </div>
            </div>
          </SoftBox>
        </Card>

        {/* Passage Title */}
        <div
          style={{
            fontSize: "18px",
            fontWeight: 700,
            borderRadius: "8px",
            color: "rgba(79, 79, 79, 1)",
            marginBottom: "10px",
            backgroundColor: "rgba(221, 225, 251, 1)",
            padding: "12px 20px",
          }}
        >
          {title}
        </div>

        {/* Passage to type */}
        <Card
          style={{
            marginBottom: "16px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
          }}
        >
          <SoftBox p={2}>
            <SoftTypography
              variant="body2"
              style={{
                fontSize: "12px",
                color: "#94a3b8",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "8px",
              }}
            >
              Text to Type
            </SoftTypography>
            {renderHighlightedPassage()}
          </SoftBox>
        </Card>

        {/* Typing Area */}
        <Card
          style={{
            marginBottom: "16px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
          }}
        >
          <SoftBox p={2}>
            <SoftTypography
              variant="body2"
              style={{
                fontSize: "12px",
                color: "#94a3b8",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "8px",
              }}
            >
              Your Input
            </SoftTypography>
            <TextField
              inputRef={textAreaRef}
              value={typedText}
              onChange={handleTyping}
              onPaste={blockEvent}
              onCopy={blockEvent}
              onCut={blockEvent}
              onDrop={blockEvent}
              onDragOver={blockEvent}
              onKeyDown={handleKeyDown}
              disabled={isFinished}
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              placeholder={
                isFinished
                  ? "Time's up! Review your results below."
                  : "Start typing here... the timer begins when you type the first character."
              }
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputProps={{
                "data-gramm": "false",
                "data-gramm_editor": "false",
                "data-enable-grammarly": "false",
                style: {
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: "16px",
                  lineHeight: "1.8",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  width: "100% !important",
                  backgroundColor: isFinished ? "#f1f5f9" : "#fff",
                },
                "& textarea": {
                  width: "100% !important",
                  padding: "12px",
                  userSelect: "text",
                  WebkitUserSelect: "text",
                },
              }}
            />
          </SoftBox>
        </Card>


        {/* Submit button */}
        <SoftBox mt={1} mb={2}>
          <SoftBox mt={2} mb={1}>
            <SoftTypography variant="body2" fontWeight="medium" color="dark">
              {isFinished
                ? "Click Next to continue to the next section."
                : "Start typing above. When finished, click Next."}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        {saveError && (
          <SoftBox mt={2} mb={1} p={2} sx={{
            border: "1px solid #B42318", borderRadius: "8px", backgroundColor: "#FEF3F2",
          }}>
            <SoftTypography variant="body2" fontWeight="bold" color="error">
              Your typing test was not saved
            </SoftTypography>
            <SoftTypography variant="body2" color="dark" mt={0.5}>
              {saveError}
            </SoftTypography>
          </SoftBox>
        )}
        <SoftBox mt={1} mb={2}>
          <SoftBox mt={2} mb={1}>
            <SoftButton
              variant="contained"
              color="gt_primary"
              onClick={handleSubmit}
              fullWidth
              disabled={
                (!isStarted && !isFinished) ||
                isSubmitting ||
                typedText.trim().length === 0
              }
              sx={{
                "&.Mui-disabled": {
                  opacity: "0.6",
                },
              }}
            >
              {isSubmitting
                ? "Submitting..."
                : nextSectionName === "Finish"
                ? "Submit"
                : "Next"}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Container>
    </Container>
  );
}
